import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiFileText, FiDownload, FiCalendar, FiDollarSign, FiHash, FiCreditCard } from "react-icons/fi";
import { title } from "../utils/style";
import fetchData from "../custom_functions/fetchData";
import { url } from "../utils/config";
import postData from "../custom_functions/postData";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/logo.png";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
});

function formatCurrency(value) {
    const numeric = Number(value);
    return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
}

function formatDate(value) {
    if (!value) return "-";
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat("en-US", {
            dateStyle: "medium",
        }).format(date);
    } catch (error) {
        return value;
    }
}

function formatDateMMDDYYYY(value) {
    if (!value) return "-";
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    } catch (error) {
        return value;
    }
}

function getStatusStyle(status) {
    switch (status) {
        case "Paid":
            return "bg-emerald-100 text-emerald-700 border-emerald-200";
        case "Pending":
            return "bg-amber-100 text-amber-700 border-amber-200";
        case "Failed":
            return "bg-red-100 text-red-700 border-red-200";
        default:
            return "bg-slate-100 text-slate-700 border-slate-200";
    }
}

function generateInvoicePdf(estimates, invoiceData) {
    const doc = new jsPDF({ compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;

    // Left Side - Logo at top
    doc.addImage(logo, "JPEG", 14, yPos, 22, 22, undefined, "FAST");

    // Left Side - Company Info (below logo)
    const companyInfoY = yPos + 24;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Roll and Charge Corporation", 14, companyInfoY);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("1-833-765-5244", 14, companyInfoY + 5);
    doc.setTextColor(0, 128, 0); // Green color for link
    doc.text("https://www.rollandcharge.com", 14, companyInfoY + 10);
    doc.setTextColor(0, 0, 0);

    // Invoice Title (centered, above Bill To section)
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", pageWidth / 2, yPos + 5, { align: "center" });

    // Right Side - Bill To Section
    const billToX = 140;
    const billToWidth = pageWidth - 14 - billToX + 2;
    const billToStartY = yPos;

    // Gray header bar for "Bill To"
    doc.setFillColor(230, 230, 230);
    doc.rect(billToX - 2, billToStartY, billToWidth, 8, "F");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Bill To", billToX, billToStartY + 5);

    // White content area with customer details
    doc.setFont("helvetica", "normal");

    // Get customer info from first estimate
    const customer = estimates[0]?.fleet_customer || {};
    
    // Helper to wrap long text
    const maxBillToWidth = billToWidth - 4; // Leave some padding
    const wrapText = (label, value) => {
        if (!value || value === "-") return [[`${label}: -`]];
        const fullText = `${label}: ${value}`;
        const lines = doc.splitTextToSize(fullText, maxBillToWidth);
        return lines.map((line, idx) => [line]);
    };

    const billToLines = [
        ...wrapText("Company Name", customer.company_name),
        ...wrapText("Name", customer.name),
        ...wrapText("Phone", customer.phone),
        ...wrapText("Email", customer.email),
    ];

    if (customer.address) billToLines.push(...wrapText("Address", customer.address));
    if (customer.city) billToLines.push(...wrapText("City", customer.city));
    if (customer.state_province) billToLines.push(...wrapText("Province", customer.state_province));
    if (customer.postal_code) billToLines.push(...wrapText("Postal Code", customer.postal_code));
    if (customer.country) billToLines.push(...wrapText("Country", customer.country));

    let billToY = billToStartY + 12;
    billToLines.forEach((lineArr) => {
        doc.text(lineArr[0], billToX, billToY);
        billToY += 4;
    });

    yPos = Math.max(companyInfoY + 15, billToY + 5);

    // Process each estimate
    let grandTotalCost = 0;
    let grandTotalTax = 0;

    estimates.forEach((estimate, index) => {
        const car = estimate.fleet_customer_car || {};

        // Check if we need a new page
        if (yPos > 220) {
            doc.addPage();
            yPos = 20;
        }

        // Vehicle Info Table
        autoTable(doc, {
            startY: yPos,
            head: [[
                { content: "VIN", styles: { halign: "center" } },
                { content: "Make", styles: { halign: "center" } },
                { content: "Model", styles: { halign: "center" } },
                { content: "Year", styles: { halign: "center" } },
                { content: "Estimated Completion Date", styles: { halign: "center" } },
            ]],
            body: [[
                car.vin || "-",
                car.make || "-",
                car.model || "-",
                car.year || "-",
                estimate.estimatedDate ? formatDateMMDDYYYY(estimate.estimatedDate) : "-",
            ]],
            theme: "grid",
            headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 7 },
            bodyStyles: { fontSize: 7, halign: "center", textColor: [0, 0, 0] },
            margin: { left: 14, right: 14 },
        });

        yPos = doc.lastAutoTable.finalY + 5;

        // Description of Work Header with border
        const amountColX = pageWidth - 40; // X position for Amount column

        // Draw the header background
        doc.setFillColor(220, 220, 220);
        doc.rect(14, yPos, pageWidth - 28, 8, "F");

        // Draw outer border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.1);
        doc.rect(14, yPos, pageWidth - 28, 8, "S");

        // Draw vertical divider line
        doc.line(amountColX, yPos, amountColX, yPos + 8);

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        // Center the header text
        const descColWidth = amountColX - 14;
        doc.text(`Description Of Work #${index + 1}`, 14 + descColWidth / 2, yPos + 5.5, { align: "center" });
        const amountColWidth = (pageWidth - 14) - amountColX;
        doc.text("Subtotal", amountColX + amountColWidth / 2, yPos + 5.5, { align: "center" });
        const headerEndY = yPos + 8;
        yPos = headerEndY;

        // Helper function to draw a section with items
        const drawSection = (sectionTitle, items, startY) => {
            if (!items || items.length === 0) return startY;

            let sectionTotal = 0;
            const tableData = items.map((item, idx) => {
                const cost = Number(item.cost) || 0;
                sectionTotal += cost;
                return [
                    idx + 1,
                    item.name || "-",
                    `$${cost.toFixed(2)}`,
                ];
            });

            const tableStartY = startY;
            autoTable(doc, {
                startY: startY,
                head: [[{ content: sectionTitle, colSpan: 3, styles: { halign: "left", fontStyle: "bold" } }], ["No.", "Name", "Cost"]],
                body: tableData,
                theme: "plain",
                headStyles: { fontStyle: "bold", fontSize: 7, halign: "left" },
                bodyStyles: { fontSize: 7, halign: "left" },
                columnStyles: {
                    0: { cellWidth: 15, halign: "left" },
                    1: { cellWidth: 80, halign: "left" },
                    2: { cellWidth: 30, halign: "left" },
                },
                margin: { left: 16, right: 50 },
                tableWidth: amountColX - 18,
            });

            const tableEndY = doc.lastAutoTable.finalY;

            // Draw borders
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.1);
            doc.line(14, tableStartY, 14, tableEndY); // Left
            doc.line(pageWidth - 14, tableStartY, pageWidth - 14, tableEndY); // Right
            doc.line(14, tableEndY, pageWidth - 14, tableEndY); // Bottom
            doc.line(amountColX, tableStartY, amountColX, tableEndY); // Vertical divider

            // Show total centered in Amount column
            const tableMidY = tableStartY + (tableEndY - tableStartY) / 2;
            const amountCenterX = amountColX + ((pageWidth - 14) - amountColX) / 2;
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text(`$${sectionTotal.toFixed(2)}`, amountCenterX, tableMidY, { align: "center" });

            return tableEndY;
        };

        // Helper function for materials/parts with labour (combined into Cost)
        const drawPartsSection = (sectionTitle, items, startY) => {
            if (!items || items.length === 0) return startY;

            let sectionTotal = 0;
            const tableData = items.map((item, idx) => {
                const cost = Number(item.cost) || 0;
                const labour = Number(item.labour) || 0;
                const itemTotal = cost + labour;
                sectionTotal += itemTotal;
                return [
                    idx + 1,
                    item.name || "-",
                    `$${itemTotal.toFixed(2)}`,
                ];
            });

            const tableStartY = startY;
            autoTable(doc, {
                startY: startY,
                head: [
                    [{ content: sectionTitle, colSpan: 3, styles: { halign: "left", fontStyle: "bold" } }],
                    ["No.", "Name", "Cost"]
                ],
                body: tableData,
                theme: "plain",
                headStyles: { fontStyle: "bold", fontSize: 7, halign: "left" },
                bodyStyles: { fontSize: 7, halign: "left" },
                columnStyles: {
                    0: { cellWidth: 15, halign: "left" },
                    1: { cellWidth: 80, halign: "left" },
                    2: { cellWidth: 30, halign: "left" },
                },
                margin: { left: 16, right: 50 },
                tableWidth: amountColX - 18,
            });

            const tableEndY = doc.lastAutoTable.finalY;

            // Draw borders
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.1);
            doc.line(14, tableStartY, 14, tableEndY);
            doc.line(pageWidth - 14, tableStartY, pageWidth - 14, tableEndY);
            doc.line(14, tableEndY, pageWidth - 14, tableEndY);
            doc.line(amountColX, tableStartY, amountColX, tableEndY);

            // Show total
            const tableMidY = tableStartY + (tableEndY - tableStartY) / 2;
            const amountCenterX = amountColX + ((pageWidth - 14) - amountColX) / 2;
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text(`$${sectionTotal.toFixed(2)}`, amountCenterX, tableMidY, { align: "center" });

            return tableEndY;
        };

        // Helper function for tires with quantity
        const drawTiresSection = (items, startY) => {
            if (!items || items.length === 0) return startY;

            let sectionTotal = 0;
            const tableData = items.map((item, idx) => {
                const cost = Number(item.cost) || 0;
                const quantity = Number(item.quantity) || 1;
                const itemTotal = cost * quantity;
                sectionTotal += itemTotal;
                
                // Format name with size if available
                let displayName = item.name || "Tire";
                if (item.tireSize && item.tireSize.trim()) {
                    displayName = `${displayName} (${item.tireSize})`;
                }
                
                return [
                    idx + 1,
                    displayName,
                    quantity,
                    `$${itemTotal.toFixed(2)}`,
                ];
            });

            const tableStartY = startY;
            autoTable(doc, {
                startY: startY,
                head: [
                    [{ content: "Tires", colSpan: 4, styles: { halign: "left", fontStyle: "bold" } }],
                    ["No.", "Name", "Quantity", "Cost"]
                ],
                body: tableData,
                theme: "plain",
                headStyles: { fontStyle: "bold", fontSize: 7, halign: "left" },
                bodyStyles: { fontSize: 7, halign: "left" },
                columnStyles: {
                    0: { cellWidth: 15, halign: "left" },
                    1: { cellWidth: 60, halign: "left" },
                    2: { cellWidth: 25, halign: "left" },
                    3: { cellWidth: 30, halign: "left" },
                },
                margin: { left: 16, right: 50 },
                tableWidth: amountColX - 18,
            });

            const tableEndY = doc.lastAutoTable.finalY;

            // Draw borders
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.1);
            doc.line(14, tableStartY, 14, tableEndY);
            doc.line(pageWidth - 14, tableStartY, pageWidth - 14, tableEndY);
            doc.line(14, tableEndY, pageWidth - 14, tableEndY);
            doc.line(amountColX, tableStartY, amountColX, tableEndY);

            // Show total
            const tableMidY = tableStartY + (tableEndY - tableStartY) / 2;
            const amountCenterX = amountColX + ((pageWidth - 14) - amountColX) / 2;
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text(`$${sectionTotal.toFixed(2)}`, amountCenterX, tableMidY, { align: "center" });

            return tableEndY;
        };

        // Draw Service Bundle section (bundleList)
        if (estimate.bundleList && estimate.bundleList.length > 0) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            yPos = drawSection("Service Bundle", estimate.bundleList, yPos);
        }

        // Draw Service List section (serviceList)
        if (estimate.serviceList && estimate.serviceList.length > 0) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            yPos = drawSection("Services", estimate.serviceList, yPos);
        }

        // Draw Parts section (materialList)
        if (estimate.materialList && estimate.materialList.length > 0) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            yPos = drawPartsSection("Parts", estimate.materialList, yPos);
        }

        // Draw Custom Services section (customList)
        if (estimate.customList && estimate.customList.length > 0) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            yPos = drawSection("Custom Services", estimate.customList, yPos);
        }

        // Draw Tires section (tireList + tire info from bundleList)
        // Extract tire info from bundles that have tireCost
        const bundleTires = (estimate.bundleList || [])
            .filter(bundle => bundle?.tireCost !== undefined && bundle?.tireCost !== null)
            .map(bundle => ({
                name: bundle.tireBrand || "Tire",
                cost: Number(bundle.tireCost) || 0,
                quantity: Number(bundle.tireQuantity) || 1,
                notes: bundle.tireNotes || "",
                tireSize: bundle.tireSize || "",
            }));
        
        // Combine regular tireList with bundle tires
        const allTires = [...(estimate.tireList || []), ...bundleTires];
        
        if (allTires.length > 0) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            yPos = drawTiresSection(allTires, yPos);
        }

        // Add estimate totals
        grandTotalCost += Number(estimate.totalCost) || 0;
        grandTotalTax += Number(estimate.tax) || 0;

        yPos += 10;
    });

    // Summary Section
    if (yPos > 220) {
        doc.addPage();
        yPos = 20;
    }

    yPos += 5;

    // Calculate subtotal (before tax)
    const subtotal = grandTotalCost - grandTotalTax;

    const summaryItems = [
        { label: "Subtotal:", value: `$${subtotal.toFixed(2)}` },
        { label: "Tax:", value: `$${grandTotalTax.toFixed(2)}` },
        { label: "Grand Total:", value: `$${grandTotalCost.toFixed(2)}` },
    ];

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    summaryItems.forEach((item, idx) => {
        if (idx === summaryItems.length - 1) {
            // Grand Total - make it stand out
            doc.setFontSize(10);
        }
        doc.text(item.label, 14, yPos);
        doc.text(item.value, pageWidth - 14, yPos, { align: "right" });
        yPos += 6;
    });

    // Save the PDF
    const fileName = `Invoice_${invoiceData?.id || "download"}_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
}

function Invoice() {
    const [invoices, setInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [isNextPage, setIsNextPage] = useState(false);

    const hasInvoices = invoices.length > 0;
    const canGoBack = page > 1;
    const canGoForward = Boolean(isNextPage);

    useEffect(() => {
        fetchInvoices(page);
    }, [page]);

    function fetchInvoices(pageNumber = 1) {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.append("page", pageNumber);

        fetchData(
            `${url}/invoices?${params.toString()}`,
            (error) => {
                setIsLoading(false);
                toast.error(error?.response?.data?.message || "Unable to load invoices", { autoClose: 3000 });
            },
            (response) => {
                setInvoices(response?.data || []);
                setIsNextPage(Boolean(response?.isNextPage));
                setIsLoading(false);
            },
            false
        );
    }

    function goToPreviousPage() {
        if (!canGoBack) return;
        setPage((prev) => Math.max(prev - 1, 1));
    }

    function goToNextPage() {
        if (!canGoForward) return;
        setPage((prev) => prev + 1);
    }

    function handleDownloadPdf(invoice) {
        if (!invoice?.id) return;

        postData(
            `${url}/invoice`, { estimateIds: invoice.estimateIds || [] },
            (error) => {
                toast.error(error?.response?.data?.message || "Unable to fetch invoice", { autoClose: 3000 });
            },
            (response) => {
                const estimates = response?.data || [];
                if (estimates.length > 0) {
                    generateInvoicePdf(estimates, invoice);
                } else {
                    toast.error("No estimate data found", { autoClose: 3000 });
                }
            },
            false
        );
    }

    return (
        <div className="p-4" style={{ backgroundColor: "#e6f9ec", borderRadius: "12px" }}>
            <div className="mb-4">
                <div className={title}>Invoice</div>
            </div>

            {/* Subtitle and Pagination */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <p className="text-emerald-600 text-sm font-medium">View and download your invoices</p>
                {!isLoading && hasInvoices && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-emerald-600">Page {page}</span>
                        <button
                            type="button"
                            onClick={goToPreviousPage}
                            disabled={!canGoBack}
                            className={`px-4 py-1.5 rounded-full border text-sm font-semibold transition-colors ${canGoBack
                                ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                }`}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={goToNextPage}
                            disabled={!canGoForward}
                            className={`px-4 py-1.5 rounded-full border text-sm font-semibold transition-colors ${canGoForward
                                ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)] hover:bg-emerald-700"
                                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                                }`}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4">
                {isLoading && (
                    <div className="rounded-2xl border border-emerald-100 bg-white px-6 py-12 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]">
                        Loading invoices...
                    </div>
                )}

                {!isLoading && !hasInvoices && (
                    <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-6 py-16 text-center shadow-[0_12px_30px_rgba(22,101,52,0.12)]">
                        <div className="flex flex-col items-center gap-4">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                                <FiFileText size={32} />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-emerald-900 mb-1">No Invoices Yet</h3>
                                <p className="text-sm text-emerald-600">Your invoices will appear here once generated.</p>
                            </div>
                        </div>
                    </div>
                )}

                {!isLoading && hasInvoices && (
                    <div className="flex flex-col gap-4">
                        {invoices.map((invoice) => (
                            <div
                                key={invoice.id}
                                className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-[0_12px_30px_rgba(22,101,52,0.12)] transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(22,101,52,0.18)]"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    {/* Left Section - Invoice Info */}
                                    <div className="flex items-start gap-4">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                                            <FiFileText size={24} />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="text-lg font-bold text-emerald-900">
                                                    Invoice #{invoice.id}
                                                </span>
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(invoice.status)}`}>
                                                    {invoice.status}
                                                </span>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                                                <div className="flex items-center gap-1.5">
                                                    <FiCalendar size={14} className="text-emerald-500" />
                                                    <span>{formatDate(invoice.date)}</span>
                                                </div>
                                                {invoice.poNumber && (
                                                    <div className="flex items-center gap-1.5">
                                                        <FiHash size={14} className="text-blue-500" />
                                                        <span>PO: {invoice.poNumber}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {invoice.invoiceDescription && (
                                                <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                                    {invoice.invoiceDescription}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Section - Amount & Actions */}
                                    <div className="flex flex-col sm:items-end gap-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-2xl font-bold text-slate-900">
                                                {formatCurrency(invoice.cost)}
                                            </span>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleDownloadPdf(invoice)}
                                                className="flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-200 bg-white text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors"
                                            >
                                                <FiDownload size={16} />
                                                <span>Download PDF</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Invoice;
